import {
  Box,
  Button,
  Card,
  FormControl,
  FormGroup,
  Grid,
  IconButton,
  Modal,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { SubmitHandler, useForm } from "react-hook-form";
import { drawdownComparisonEditModalStyles } from "../../../../styles/DrawdownComparisonChart.styles";

interface Props {
  isOpened: boolean;
  onClose: () => void;
  maxDrop: number;
  negativeLimit: number;
  onMaxDropChange: (maxDrop: number) => void;
  onNegativeLimitChange: (negativeLimit: number) => void;
}

interface MaxDropInputs {
  maxDrop: number;
  negativeLimit: number;
}

const defaultValues: MaxDropInputs = {
  maxDrop: 20,
  negativeLimit: -20,
};

export const DrawdownComparisonEditModal = ({
  isOpened,
  onClose,
  maxDrop,
  negativeLimit,
  onMaxDropChange,
  onNegativeLimitChange,
}: Props) => {
  const { register, handleSubmit, reset } = useForm<MaxDropInputs>({
    defaultValues: { maxDrop, negativeLimit },
  });

  const closeModal = () => {
    onClose();
    reset({ maxDrop, negativeLimit });
  };

  const handleFormSubmit: SubmitHandler<MaxDropInputs> = (data) => {
    onMaxDropChange(data.maxDrop);
    onNegativeLimitChange(data.negativeLimit);
    closeModal();
  };

  useEffect(() => {
    reset({ maxDrop, negativeLimit });
  }, [maxDrop, negativeLimit, reset]);

  return (
    <Modal open={isOpened} onClose={closeModal}>
      <Stack height="100%" alignItems="center" justifyContent="center">
        <Box
          sx={drawdownComparisonEditModalStyles.modalContainer}
          component="form"
          onSubmit={handleSubmit(handleFormSubmit)}
        >
          <Card>
            <Grid container justifyContent="space-between" alignItems="center">
              <Grid item>
                <Typography variant="h5" component="h5" sx={{ padding: 1 }}>
                  Edit Drawdown Limit
                </Typography>
              </Grid>
              <Grid item>
                <IconButton onClick={closeModal} aria-label="Close modal">
                  <CloseIcon />
                </IconButton>
              </Grid>
            </Grid>
            <FormGroup>
              <FormControl sx={{ margin: 1 }}>
                <TextField
                  label="Maximum Drop (%)"
                  type="number"
                  inputProps={{ step: "1", min: "1" }}
                  {...register("maxDrop", {
                    required: true,
                    valueAsNumber: true,
                    min: {
                      value: 1,
                      message: "Maximum drop must be at least 1%",
                    },
                  })}
                  helperText={`Current maximum drop: ${maxDrop.toFixed(1)}%`}
                />
              </FormControl>
              <FormControl sx={{ margin: 1 }}>
                <TextField
                  label="Negative Limit (%)"
                  type="number"
                  inputProps={{ step: "1", max: "-1" }}
                  {...register("negativeLimit", {
                    required: true,
                    valueAsNumber: true,
                    max: {
                      value: -1,
                      message:
                        "Negative limit must be less than or equal to -1%",
                    },
                  })}
                  helperText={`Current negative limit: ${negativeLimit.toFixed(1)}%`}
                />
              </FormControl>
              <Box sx={drawdownComparisonEditModalStyles.buttonsContainer}>
                <Button
                  type="button"
                  variant="outlined"
                  onClick={closeModal}
                  sx={drawdownComparisonEditModalStyles.button}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  sx={drawdownComparisonEditModalStyles.button}
                >
                  Save
                </Button>
              </Box>
            </FormGroup>
          </Card>
        </Box>
      </Stack>
    </Modal>
  );
};
